import {
  ApiError,
  FetchVenueFeatureFlagRequest,
  PagedByVenueRequest,
} from "@greeter/api";
import {
  Booking,
  BookingSettings,
  Bundle,
  Customer,
  FloorPlan,
  GreeterEvent,
  LockedTable,
  Menu,
  Product,
  SpecialOpeningHours,
  SpecialPricePeriod,
  TableId,
  TableServiceOrder,
  TableServiceSettings,
  Venue,
  VenueFeatureFlag,
} from "@greeter/core";
import { partial, uniq } from "lodash";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useGreeterGuestApi, useAuth } from "@greeter/matter";
import { DatePeriod, TimeOfDay } from "@greeter/date";
import { mapQuery, Query } from "@greeter/util";
import { useMemo } from "react";

export function useVenueQuery(venueId: string) {
  const { api } = useGreeterGuestApi();
  return useQuery<Venue, Error, Venue, string[]>({
    queryKey: ["venue", venueId],
    async queryFn() {
      return await api.fetchVenue(venueId);
    },
  });
}
export function useVenuesQuery(venueIds: string[] = []) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["venues", ...uniq(venueIds)],
    queryFn: async () => await api.fetchVenues({ ids: venueIds }),
    enabled: venueIds && venueIds.length > 0,
  });
}
export function useGreeterEventsQuery(venueId?: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["greeterEvents", venueId],
    queryFn: async () =>
      venueId
        ? await api.fetchUpcomingGreeterEventsForVenue({ venueId })
        : await api.fetchUpcomingGreeterEvents({
            city: "Aalborg",
            country: "Denmark",
          }),
  });
}
export function useGreeterEventsByCityQuery(country: string, city: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["greeterEvents", country, city],
    queryFn: async () =>
      await api.fetchUpcomingGreeterEvents({
        city: city,
        country: country,
      }),
  });
}
export function useActivitiesQuery(venueId: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["activities", venueId],
    queryFn: async () => await api.fetchVenueActivities(venueId),
  });
}
export function useThemesQuery(venueId: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["themes", venueId],
    queryFn: async () => await api.fetchThemesByVenue(venueId),
  });
}
export function useBundlesQuery(venueId: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["bundles", venueId],
    queryFn: async () => await api.fetchBundlesByVenue(venueId),
  });
}
export function useProductsQuery(venueId: string) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["products", venueId],
    queryFn: async () => await api.fetchProductsByVenue(venueId),
  });
}
export function useDaysliceConfigQuery() {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["daysliceConfig"],
    queryFn: async () => await api.fetchDaysliceConfig(),
    enabled: false, // Just use the placeholder data
    placeholderData: {
      dayOffset: 0,
      dayStart: 0,
      eveningStart: 18,
      nightStart: 22,
    },
  });
}
export function useCustomerQuery(
  options?:
    | Partial<UseQueryOptions<Customer, ApiError, Customer, string[]>>
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["customer"],
    queryFn: async () => await api.fetchCustomer(),
    retry: Infinity,
    ...options,
  });
}

export function useTableServiceOrderQuery(
  id: string,
  options?:
    | Omit<
        UseQueryOptions<TableServiceOrder, Error, TableServiceOrder, string[]>,
        "queryKey" | "queryFn"
      >
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["tableServiceOrder", id],
    queryFn: async () => await api.fetchTableServiceOrder(id),
    ...options,
  });
}

export function useCitiesQuery(
  country: string,
  options?:
    | Omit<
        UseQueryOptions<string[], Error, string[], string[]>,
        "queryKey" | "queryFn"
      >
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["cities"],
    queryFn: async () => await api.fetchCities(country),
    ...options,
  });
}

export function useMenuQuery(
  venueId?: string,
  options?: UseQueryOptions<Menu, Error, Menu, any[]>
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["menu", venueId],
    queryFn: () => api.fetchMenu(venueId ?? ""),
    ...options,
  });
}

export function useTableServiceSettingsQuery(
  venueId?: string,
  options?:
    | Omit<
        UseQueryOptions<
          TableServiceSettings,
          Error,
          TableServiceSettings,
          (string | undefined)[]
        >,
        "queryKey" | "queryFn"
      >
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["tableServiceSettings", venueId],
    queryFn: async () => {
      if (venueId) return await api.fetchTableServiceSettings(venueId);

      return { mode: "open" } as TableServiceSettings;
    },
    placeholderData: { mode: "open" },
    ...options,
  });
}

export function useVenueFeatureFlag(request: FetchVenueFeatureFlagRequest) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["venueFeatureFlag", request.venueId, request.name],
    async queryFn() {
      return await api.fetchVenueFeatureFlag(request);
    },
  });
}

const defaultBookingSettings: BookingSettings = {
  buffer: TimeOfDay.parse("01:00:00"),
};

export function useBookingSettingsQuery(
  venueId?: string,
  options?:
    | UseQueryOptions<BookingSettings, Error, BookingSettings, any[]>
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["bookingSettings", venueId],
    queryFn: async () => {
      if (venueId) return await api.fetchBookingSettings(venueId);
      else return defaultBookingSettings;
    },
    // Abusing TimeOfDay as an interval
    placeholderData: defaultBookingSettings,
    ...options,
  });
}

export type UseBookingsQueryArgs = {
  period?: DatePeriod;
};
export function useBookingsQuery(
  args: UseBookingsQueryArgs,
  options?: UseQueryOptions<Booking[], Error, Booking[], any[]>
) {
  const { api } = useGreeterGuestApi();
  const { isLoggedIn } = useAuth();
  return useQuery({
    queryKey: ["bookings", args, isLoggedIn],
    queryFn: () => api.fetchBookings(),
    enabled: isLoggedIn,
    ...options,
  });
}

function useSimpleQuery<T>(reactQuery: UseQueryResult<T, Error>): Query<T> {
  return useMemo(() => mapQuery(reactQuery), [reactQuery]);
}

const lockedTableKeys = {
  get: (venueId?: string, date?: Date) => [venueId, date],
};

export function useGreeterEventQuery(
  id: string,
  options?: UseQueryOptions<GreeterEvent, Error, GreeterEvent, string[]>
) {
  const { api: proxy } = useGreeterGuestApi();

  return useQuery({
    queryKey: ["greeterEvent", id],
    queryFn: partial(proxy.fetchGreeterEvent.bind(proxy), id),
    ...options,
  });
}

export function useGreeterEventsForVenueQuery(
  args: PagedByVenueRequest,
  options?:
    | Partial<UseQueryOptions<GreeterEvent[], Error, GreeterEvent[], any[]>>
    | undefined
) {
  const { api: proxy } = useGreeterGuestApi();

  return useQuery({
    queryKey: ["greeterEvents", args.venueId],
    queryFn: partial(
      proxy.fetchUpcomingGreeterEventsForVenue.bind(proxy),
      args
    ),
    ...options,
  });
}

export function useBundlesByVenueQuery(
  venueId?: string,
  options?: UseQueryOptions<Bundle[], Error, Bundle[], string[]> | undefined
) {
  const { api: proxy } = useGreeterGuestApi();

  return useQuery({
    queryKey: ["bundles", venueId ?? ""],
    queryFn: partial(proxy.fetchBundlesByVenue.bind(proxy), venueId!),
    enabled: !!venueId,
    ...options,
  });
}

export function useProductsByVenueQuery(
  venueId?: string,
  options?: UseQueryOptions<Product[], Error, Product[], string[]> | undefined
) {
  const { api: proxy } = useGreeterGuestApi();

  return useQuery({
    queryKey: ["products", venueId ?? ""],
    queryFn: partial(proxy.fetchProductsByVenue.bind(proxy), venueId!),
    enabled: !!venueId,
    ...options,
  });
}

export function useSpecialPricePeriods(
  productIds?: Array<string>,
  options?:
    | UseQueryOptions<
        Array<SpecialPricePeriod>,
        Error,
        Array<SpecialPricePeriod>,
        string[]
      >
    | undefined
) {
  const { api } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["productSpecialPricePeriods", ...(productIds ?? [])],
    queryFn() {
      if (productIds) {
        return api.fetchSpecialPricePeriods(productIds);
      } else {
        return [];
      }
    },
    enabled: !!productIds,
    ...options,
  });
}

export function useSpecialOpeningHoursQuery(
  venueId?: string,
  options?:
    | UseQueryOptions<
        SpecialOpeningHours[],
        Error,
        SpecialOpeningHours[],
        string[]
      >
    | undefined
) {
  const { api } = useGreeterGuestApi();

  const q = useQuery({
    queryKey: ["venue", venueId ?? "", "specialOpeningHours"],
    queryFn: async () => {
      if (!!venueId) return await api.fetchSpecialOpeningHours(venueId);
      else return [];
    },
    enabled: !!venueId,
    ...options,
  });
  const simple = useSimpleQuery(q);

  return simple;
}

export function useFloorPlanQuery(
  venueId?: string,
  options?: UseQueryOptions<FloorPlan, Error, FloorPlan, string[]>
) {
  const { api: proxy } = useGreeterGuestApi();
  return useQuery({
    queryKey: ["floorplan", venueId ?? ""],
    queryFn: partial(proxy.fetchFloorPlan.bind(proxy), venueId!),
    enabled: !!venueId,
    ...options,
  });
}

const dateFormatter = Intl.DateTimeFormat("da-DK", {
  year: "numeric",
  day: "numeric",
  month: "numeric",
});

export function useBookedTablesQuery(
  venueId?: string,
  selectedDate?: Date,
  options?:
    | UseQueryOptions<TableId[], Error, TableId[], (string | undefined)[]>
    | undefined
) {
  const { api } = useGreeterGuestApi();

  return useQuery({
    queryKey: [
      "bookedTables",
      venueId,
      selectedDate && dateFormatter.format(selectedDate),
    ],
    queryFn() {
      return api.fetchBookedTables(venueId!, selectedDate!);
    },
    enabled: !!venueId && !!selectedDate,
    refetchInterval: 2000,
    ...options,
  });
}

export function useLockedTablesQuery(
  venueId?: string,
  date?: Date,
  options?: UseQueryOptions<LockedTable[], Error, LockedTable[], any[]>
) {
  const { api } = useGreeterGuestApi();

  const q = useQuery({
    queryKey: lockedTableKeys.get(venueId, date),
    queryFn: async () => {
      if (venueId && date) return await api.fetchLockedTables(venueId, date);
      return [];
    },
    enabled: !!venueId && !!date,
    refetchInterval: 2000,
    ...options,
  });

  const simpleQuery = useMemo(() => mapQuery(q), [q]);

  return simpleQuery;
}

export type FeatureFlag = "upsell.v1.guest";

export function useFeatureFlagQuery(venueId: string, featureFlag: FeatureFlag) {
  const { api } = useGreeterGuestApi();
  const q = useQuery({
    queryKey: ["featureFlag", venueId, featureFlag],
    async queryFn() {
      return await api.fetchVenueFeatureFlag({
        venueId,
        name: featureFlag as string,
      });
    },
  });
  const simpleQuery = useMemo(() => mapQuery(q), [q]);
  return simpleQuery;
}
